package com.evoapps.evoshift.domain.model

import kotlinx.serialization.Serializable

@Serializable
data class Roster(
    val id: String,
    val workplaceId: String,
    val month: Int,
    val year: Int,
    val status: RosterStatus, // SKELETON, DRAFT, PUBLISHED, LOCKED
    val currentVersion: Int,
    val metadata: AuditMetadata,
    val availabilityRequestId: String? = null
) {
    fun getMonthYear(): String {
        val monthNum = if (month < 10) "0$month" else month.toString()
        return "$year-$monthNum"
    }
}

@Serializable
enum class RosterStatus { SKELETON, DRAFT, PUBLISHED, LOCKED, ARCHIVED }

@Serializable
data class RosterFinancials(
    val rosterId: String,
    val planned: FinancialSummary,
    val actual: FinancialSummary,
    val combined: FinancialSummary,
    val costs: List<FixedCost> = emptyList(), // Materialized from rules
    /** The month's days with a daily record (SH-46; 0 in totals written before it). */
    val days: Int = 0,
    /** Of [days], those with takings entered: what the actual covers (SH-46). */
    val daysWithTakings: Int = 0
)

/**
 * One side of a month's totals (roster_financials, computed by the recalculateRosterFinancials
 * Cloud Function): plan, actual to date, or combined (actual where the takings are in, plan for
 * the rest).
 */
@Serializable
data class FinancialSummary(
    /** Employer labour cost of the shifts (an estimate). */
    val laborCost: Double,
    /** The month's ledger costs: the rules' fixed costs and the one-off costs (SH-87: not its revenue items). */
    val otherCosts: Double = 0.0,
    /** Net sales: the targets (plan) or the takings (actual). Fixed revenue is not included. */
    val revenue: Double,
    /** Labour cost ÷ net sales × 100 (kept for older readers; the screens show [labourPercent]). */
    val laborPercentage: Double = 0.0,
    /** The commission the owner earns on the net sales (SH-46): each day's sales × its rate. */
    val commission: Double = 0.0,
    /** The workplace's fixed monthly revenue, whole, in every side (SH-46). */
    val fixedRevenue: Double = 0.0,
    /** SH-87: the month's ledger revenue: recurring and one-off revenue items, whole, in every side. */
    val otherRevenue: Double = 0.0,
    /** SH-95: what the month's labour % is measured against; null in totals written before SH-95. */
    val laborBase: LabourBase? = null,
    /**
     * SH-95: the amount labour is measured against, the sum of the days' ([LabourShare]): their
     * commission plus their share of the fixed revenue, or their net sales without a commission.
     * Null in totals written before SH-95.
     */
    val laborIncome: Double? = null,
    /**
     * The owner's income from the sales: the sum of the days' commission, or of their net sales on
     * days without a commission ([LabourShare.salesIncome]). Null in totals written before it.
     */
    val salesIncome: Double? = null
) {
    /**
     * [salesIncome], or for older totals what their fields allow: the commission when there is
     * one, else the net sales of a workplace without a commission (labour measured on the sales).
     * (An older month whose commission started midway counts only its commission until recalculated.)
     */
    val salesIncomeAmount: Double
        get() = salesIncome ?: when {
            commission > 0 -> commission
            labourBase == LabourBase.SALES -> revenue
            else -> 0.0
        }

    /** What [labourPercent] is measured against (derived from the commission in older totals). */
    val labourBase: LabourBase get() = laborBase ?: LabourShare.baseOf(commission)

    /** The month's income for the labour % (older totals: commission + fixed revenue, or the net sales). */
    val labourBaseAmount: Double
        get() = laborIncome ?: if (commission > 0) commission + fixedRevenue else revenue

    /** Labour cost as a share of the month's income (SH-95); null without income. [laborPercentage] stays % of net sales. */
    val labourPercent: Double? get() = LabourShare.percent(laborCost, labourBaseAmount)

    /**
     * SH-147: the fixed revenue this side's labour income holds: the shares of its days (the
     * actual side: the days with takings), i.e. [laborIncome] − [salesIncome]; [fixedRevenue]
     * (whole) for older totals without them.
     */
    val fixedRevenueToDate: Double
        get() {
            val income = laborIncome ?: return fixedRevenue
            val sales = salesIncome ?: return fixedRevenue
            return kotlin.math.round((income - sales).coerceIn(0.0, fixedRevenue) * 100) / 100.0
        }
}

@Serializable
data class FixedCost(
    val id: String,
    val name: String,
    val amount: Double,
    val currency: String,
    val date: String,
    val sourceType: CostSourceType, // GENERATED_FROM_RULE, MANUAL_ONE_OFF
    /** A one-off expense's note (SH-84), e.g. what was repaired. */
    val note: String? = null,
    /** The rule a cost came from (a generated cost, or a migrated one-off rule). */
    val originRuleId: String? = null,
    /** SH-87: a cost or revenue of the month; items saved before are costs. */
    val kind: LedgerKind = LedgerKind.COST,
    /** SH-131: this workplace's part of an owner's shared cost (sourceType ALLOCATED_FROM_OWNER). */
    val allocation: CostAllocation? = null,
    /**
     * A one-off item covering several days of the month: its last day (inclusive), [date] its
     * first. Null (every document saved before) is the single day [date].
     */
    val endDate: String? = null,
    /** SH-195: the rule's (or shared cost's) frequency it was materialised from; null on items from before. */
    val frequency: CostFrequency? = null
) {
    /** The last day the item covers: [endDate], or [date] for a single day. */
    val lastDate: String get() = endDate?.takeIf { it > date } ?: date

    /** Covers more than one day ([date] to [endDate]). */
    val isPeriod: Boolean get() = lastDate != date

    /**
     * SH-195: a month's recurring item (a monthly or yearly rule's cost or revenue, or a part of
     * a shared cost) counts an equal part on every day of its month in the daily result, so each
     * day's result carries its share of the rent; a one-off item (or a weekly rule's) stays on its
     * day (or its period). Only the daily result spreads it ([resultShareOn], DayResults.resultDay):
     * the stored daily records keep it whole on its date ([amountOn]).
     */
    val spreadsOverMonth: Boolean
        get() = !isOneOff && endDate == null && when (frequency) {
            CostFrequency.MONTHLY, CostFrequency.YEARLY -> true
            CostFrequency.WEEKLY, CostFrequency.ONE_OFF -> false
            // Items from before the frequency was kept: a month's charge is dated on its 1st.
            null -> date.endsWith("-01")
        }

    /** The days the item counts on in the daily records, in order (one for a single day). */
    val days: List<String>
        get() = LedgerSpread.days(date, lastDate)

    /**
     * The part of [amount] that falls on [day] in the daily records (daily_records costs.otherCosts
     * and revenue.otherRevenue, as old app versions and the Cloud Functions count it): all of it
     * on its day for a single day; for a period an equal part of every day in whole cents, the
     * last day the remainder, so the parts add up to [amount] exactly (as the fixed monthly
     * revenue is split, LabourShare). The month's totals count [amount] once.
     */
    fun amountOn(day: String): Double = LedgerSpread.share(amount, date, lastDate, day)

    /**
     * SH-195: the part of [amount] the daily result counts on [day]: [amountOn], except that a
     * month's recurring item ([spreadsOverMonth]) is an equal part of every day of its month in
     * whole cents, the last day the remainder. Never stored.
     */
    fun resultShareOn(day: String): Double {
        if (!spreadsOverMonth) return amountOn(day)
        val first = LedgerSpread.monthStart(date) ?: return amountOn(day)
        val last = LedgerSpread.monthEnd(date) ?: return amountOn(day)
        return LedgerSpread.share(amount, first, last, day)
    }

    val isRevenue: Boolean get() = kind == LedgerKind.REVENUE

    /** SH-131: a part of a shared cost of the business: read-only here, changed in the owner's shared costs. */
    val isAllocated: Boolean get() = sourceType == CostSourceType.ALLOCATED_FROM_OWNER

    /** A one-off expense of the month: it can be changed and removed; a rule's cost is the month's snapshot. */
    val isOneOff: Boolean get() = sourceType == CostSourceType.MANUAL_ONE_OFF
}

@Serializable
enum class CostSourceType {
    GENERATED_FROM_RULE,
    MANUAL_ONE_OFF,
    /** SH-131: a workplace's part of an owner's shared cost, written by the server only. */
    ALLOCATED_FROM_OWNER
}

/** A one-off expense that cannot be saved (SH-84): the domain says which part is wrong. */
class InvalidOneOffCostException(val problem: Problem) : Exception("Invalid one-off cost: $problem") {
    enum class Problem { NAME_MISSING, AMOUNT_NOT_POSITIVE, DATE_OUTSIDE_MONTH, PERIOD_ENDS_BEFORE_START }
}

/** Only one-off expenses are changed or removed by hand; a rule's cost belongs to the month's snapshot. */
class NotAOneOffCostException(val costId: String) : Exception("Cost $costId is not a one-off expense")

/**
 * The month "now" is about: the roster of [year]/[month], else the latest one before it, else the
 * earliest after it (a workplace that has only planned ahead). Never "the newest", which is
 * usually a draft or skeleton of a future month.
 */
fun List<Roster>.currentOrNearest(year: Int, month: Int): Roster? {
    val now = year * 100 + month
    fun key(r: Roster) = r.year * 100 + r.month
    return firstOrNull { key(it) == now }
        ?: filter { key(it) < now }.maxByOrNull(::key)
        ?: filter { key(it) > now }.minByOrNull(::key)
}
